import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAppContext } from "../libs/contextLib";
import Button from 'react-bootstrap/Button';
import { onError } from "../libs/errorLib";
import * as HelperUtils from "../libs/utilsLib";
import { saveBusiness } from "../services/Business";
import * as ClassificationConstants from "../constants/classificationsConstants";
import * as SocialConstants from "../constants/socialPlatformConstants";
import "./Profile.css";

export default function Profile() {
    const { isAuthenticated, business, } = useAppContext();
    const [, setIsLoading] = useState(false);
    const history = useHistory();
    const NONE_LISTED = "None Listed";

    useEffect(() => {
        //ensures window scrolls to top when loading this page
        window.scrollTo(0, 0);
        
        async function onLoad() {
            if (!isAuthenticated) {
                return;
            }

            // track user viewed page on load
            setIsLoading(true);
            try {
                const params = {};
                params["hasViewedProfile"] = true;
                await saveBusiness(business.businessId, params);
                setIsLoading(false);
            } catch (e) {
                onError(e);
                setIsLoading(false);
            }
        }

        onLoad();
    }, [business.businessId, isAuthenticated]);

    async function handleSubmit(event, destination) {
            event.preventDefault();
            event.stopPropagation();

            // Save Acknowledgement
            setIsLoading(true);
            try {
                const params = {};
                params["hasAcknowledgedProfile"] = true;
                await saveBusiness(business.businessId, params);
                setIsLoading(false);
            } catch (e) {
                onError(e);
                setIsLoading(false);
            }

            if (destination === "next") {
                history.push("stamps");
            } else if (destination === "home") {
                history.push("home");
            } 
        }
    function renderClassifications() {
        return (
            <>
                { business && business.classifications && business.classifications.length > 0 &&
                    HelperUtils.getSortedDecalForDisplay(business.classifications).map((classification, index) => {
                        return (
                            <div key={"classification-"+index}>{classification}</div>
                        )
                    })
                }
            </>
        );
    }

    function renderWebsites() {
        let sites = [];
        if (business.website) {
            sites.push(business.website);
        }
        if (business.websites) {
            // application.websites is array of { value: "url string"}
            sites.push.apply(sites, business.websites.map((x) => x.value));
        }
        return (
            <>
                { sites && sites.length > 0 && 
                    sites.map((site, index) => {
                        return (
                            <div key={"site-"+index}><a href={site} target="_blank" rel="noopener noreferrer">{HelperUtils.prefixUrl(site)}</a></div>
                        )
                })
                }
                {  (!sites || (sites && sites.length < 1)) && (
                    <div>{NONE_LISTED}</div> 
                )}
            </>
        );
    }

    function renderCoOwners() {
        return (
            <>
                { /* Addtional Platforms, if available */ 
                    business.coOwners && business.coOwners.length > 0 && 
                    business.coOwners.map((owner, index) => {
                        return (
                            <div key={"cowowner-"+index}>{owner.firstName} {owner.lastName}</div>
                        )
                })
                }
                {  (!business.coOwners || (business.coOwners && business.coOwners.length < 1)) && (
                    <div>{NONE_LISTED}</div> 
                )}
            </>
        );
    }

    function renderPlatforms(platforms, platformName) {
        if (!platforms) return;
        // reduce the platforms 
        let filteredPlatforms = [];
        if (platforms && platforms.length > 0) {
            filteredPlatforms = platforms.filter(function(platform){
                return platform.name.toLowerCase() === platformName.toLowerCase();
            });
        }
        return (
            <>
                { /* Addtional Platforms, if available */ 
                    filteredPlatforms && filteredPlatforms.length > 0 && 
                    filteredPlatforms.map((platform, index) => {
                        let potentialUrl = HelperUtils.renderSocialUrl(platform.name, platform.value);
                        return (
                            <div key={"platform-"+platformName+"-"+index}>
                                <a href={potentialUrl} target="_blank" rel="noopener noreferrer">{potentialUrl}</a>
                            </div>
                        )
                })
                }
                {  (!filteredPlatforms || (filteredPlatforms && filteredPlatforms.length < 1)) && (
                    <div>{NONE_LISTED}</div> 
                )}
            </>
        );
    }

    function parseCountry() {
        if (business.country && business.country !== 'US') {
            return HelperUtils.getCountry(business.country);
        } else {
            return null;
        }
    }

    function isSmallBusiness(business) {
        return HelperUtils.hasClassification(business?.classifications, ClassificationConstants.SMALL_BIZ) || 
            HelperUtils.hasBusinessTerms(business?.termsData, ClassificationConstants.TERMS_DATA_TYPES.SMALLBIZ) 
            ? 'Yes' : 'No';
    }

    return (
        <div>
            {isAuthenticated && business && (
                <div className="profileContainer">
                    <div className="profile row-bottom-20">
                        <div>
                            <div className="row">
                                <h5>Account Information</h5>
                            </div>
                            <div className="row">
                                <div className="column">Business Name:</div>
                                <div>{ business.name }</div>
                            </div>
                            { business.dbaName && (
                                <div className="row">
                                    <div className="column">DBA Name:</div>
                                    <div>{ business.dbaName }</div>
                                </div>
                            )}
                            <div className="row">
                                <div className="column">Address:</div>
                                <div>{business.address1}, {business.address2 ? business.address2 + ',' : null} {business.city}, {business.state} {business.zip} {parseCountry()}</div>
                            </div>
                            { business.partnerCode && (
                                <div className="row">
                                    <div className="column">Partner:</div>
                                    <div>{ HelperUtils.capitalizeFirstLetter(business.partnerCode) }</div>
                                </div>
                            )}
                            <div className="row">
                                <div className="column">Certifications:</div>
                                <div>{ renderClassifications() }</div>
                            </div>
                            <div className="row">
                                <div className="column">Name:</div> 
                                <div>{business.ownerFirstName} {business.ownerLastName}</div>
                            </div>
                            <div className="row">
                                <div className="column">Email:</div> 
                                <div>{ business.ownerEmail }</div>
                            </div>
                            <div className="row">
                                <div className="column">U.S. Citizen:</div> 
                                <div>{HelperUtils.parseTermField(business?.termsData?.formAgreeCitizen)}</div>
                            </div>
                            <div className="row">
                                <div className="column">Co-Owner(s):</div> 
                                <div>{renderCoOwners()}</div>
                            </div>
                            <div className="row">
                                <div className="column">Own 51% or more of Business:</div> 
                                <div>{HelperUtils.parseTermField(business?.termsData?.formAgreeOwnership)}</div>
                            </div>
                            <div className="row">
                                <div className="column">Small Business (&lt;250 employees):</div> 
                                <div>{isSmallBusiness(business)}</div>
                            </div>
                            
                        </div>
                        <div>
                            <div className="row">
                                <h5>Domain Information</h5>
                            </div>
                            <div className="row">
                                <div className="column">Website:</div> 
                                <div>{ renderWebsites() }</div>
                            </div>
                            <div className="row">
                                <div className="column">{SocialConstants.FACEBOOK}:</div> 
                                <div>{ renderPlatforms(business.platforms, SocialConstants.FACEBOOK) }</div>
                            </div>
                            <div className="row">
                                <div className="column">{SocialConstants.INSTAGRAM}:</div> 
                                <div><div>{ renderPlatforms(business.platforms, SocialConstants.INSTAGRAM) }</div></div>
                            </div>
                            <div className="row">
                                <div className="column">{SocialConstants.PINTEREST}:</div>
                                <div><div>{ renderPlatforms(business.platforms, SocialConstants.PINTEREST) }</div></div> 
                            </div>
                            <div className="row">
                                <div className="column">{SocialConstants.TIKTOK}:</div> 
                                <div><div>{ renderPlatforms(business.platforms, SocialConstants.TIKTOK) }</div></div>
                            </div>
                            <div className="row">
                                <div className="column">{SocialConstants.TWITCH}:</div> 
                                <div><div>{ renderPlatforms(business.platforms, SocialConstants.TWITCH) }</div></div>
                            </div>
                            <div className="row">
                                <div className="column">{SocialConstants.X}:</div> 
                                <div><div>{ renderPlatforms(business.platforms, SocialConstants.X) }</div></div>
                            </div>
                            <div className="row">
                                <div className="column">{SocialConstants.YOUTUBE}:</div> 
                                <div><div>{ renderPlatforms(business.platforms, SocialConstants.YOUTUBE) }</div></div>
                            </div>
                            <div className="row">
                                <div className="column">{SocialConstants.OTHER}:</div> 
                                <div><div>{ renderPlatforms(business.platforms, SocialConstants.OTHER) }</div></div>
                            </div>
                        </div>
                    </div>
                    <div className="profileText row-top-30 row-bottom-30">
                        If any of the information above needs to be changed, please reach out to <a href="mailto:support@sertify.io">support@sertify.io</a>
                    </div>
                    <div className="pageButtons-single row-top-10">
                        <Button variant="primary" onClick={event => handleSubmit(event, 'home')}>Acknowledge</Button>
                    </div>
                </div>
            )}
        </div>
    );
}